import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { openBookingModal } from '../components/BookingModal';
import { 
  Building2, AlertTriangle, Sparkles, TrendingUp,
  CheckCircle2, ChevronDown, ChevronRight, Loader2,
  Phone, MessageSquare, Workflow, Database, Calendar, BarChart3,
  Clock, Users, RefreshCw, ArrowRight, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProposalView = () => {
  const { proposalId } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const fetchProposal = async () => {
      try {
        const response = await fetch(`${API_URL}/api/proposal/${proposalId}`);
        if (response.ok) {
          const data = await response.json();
          setProposal(data);
        } else if (response.status === 404) {
          setError('Proposal not found');
        } else {
          throw new Error('Failed to load proposal');
        }
      } catch (err) {
        setError('Unable to load proposal. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProposal();
  }, [proposalId]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getIcon = (iconName) => {
    const icons = {
      clock: Clock,
      moon: Clock,
      users: Users,
      filter: Zap,
      phone: Phone,
      message: MessageSquare,
      workflow: Workflow,
      database: Database,
      calendar: Calendar,
      chart: BarChart3
    };
    return icons[iconName] || Zap;
  };

  if (loading) {
    return (
      <div className="proposal-view-page" data-testid="proposal-view-page">
        <div className="proposal-loading">
          <Loader2 className="w-12 h-12 animate-spin" />
          <p>Loading your proposal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="proposal-view-page" data-testid="proposal-view-page">
        <div className="proposal-error">
          <AlertTriangle className="w-12 h-12" />
          <h2>{error}</h2>
          <p>The proposal you're looking for may have expired or doesn't exist.</p>
          <Link to="/" className="btn-gradient">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const auditData = proposal?.audit_data || {};

  return (
    <div className="proposal-view-page" data-testid="proposal-view-page">
      {/* Header */}
      <section className="proposal-header">
        <div className="container">
          <motion.div 
            className="proposal-header-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="proposal-badge">
              <Sparkles className="w-4 h-4" />
              <span>Custom AI Proposal</span>
            </div>
            <h1>Your AI Revenue Engine Proposal</h1>
            <p>Prepared for <strong>{proposal?.first_name}</strong> • {auditData.business_profile?.name}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="proposal-content">
        <div className="container">
          {/* Business Profile */}
          <motion.div
            className="audit-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="section-header">
              <div className="section-icon blue">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h2>Business Profile</h2>
                <p>What we discovered about your business</p>
              </div>
            </div>
            
            <div className="profile-grid">
              <div className="profile-card">
                <span className="profile-label">Business</span>
                <span className="profile-value">{auditData.business_profile?.name}</span>
              </div>
              <div className="profile-card">
                <span className="profile-label">Industry</span>
                <span className="profile-value">{auditData.business_profile?.industry}</span>
              </div>
              <div className="profile-card">
                <span className="profile-label">Est. Monthly Leads</span>
                <span className="profile-value">{auditData.business_profile?.estimated_monthly_leads}</span>
              </div>
              <div className="profile-card highlight">
                <span className="profile-label">Current Response Time</span>
                <span className="profile-value warning">{auditData.business_profile?.current_response_time}</span>
              </div>
              <div className="profile-card highlight">
                <span className="profile-label">Operating Hours</span>
                <span className="profile-value warning">{auditData.business_profile?.operating_hours}</span>
              </div>
            </div>
          </motion.div>

          {/* Revenue Leaks */}
          <motion.div
            className="audit-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="section-header">
              <div className="section-icon red">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h2>Revenue Leaks Identified</h2>
                <p>Critical gaps costing you money every month</p>
              </div>
            </div>
            
            <div className="leaks-grid">
              {auditData.revenue_leaks?.map((leak, index) => {
                const LeakIcon = getIcon(leak.icon);
                return (
                  <div key={index} className="leak-card">
                    <div className="leak-icon">
                      <LeakIcon className="w-5 h-5" />
                    </div>
                    <div className="leak-content">
                      <h4>{leak.title}</h4>
                      <p>{leak.description}</p>
                      <span className="leak-impact">{leak.impact}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Custom Buildout */}
          <motion.div
            className="audit-section buildout-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="section-header">
              <div className="section-icon purple">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h2>Your Custom AI Buildout</h2>
                <p>{auditData.custom_buildout?.summary}</p>
              </div>
            </div>
            
            <div className="buildout-grid">
              {auditData.custom_buildout?.components?.map((component, index) => {
                const ComponentIcon = getIcon(component.icon);
                const isExpanded = expandedSections[`buildout-${index}`];
                
                return (
                  <div
                    key={index}
                    className={`buildout-card ${isExpanded ? 'expanded' : ''}`}
                  >
                    <div className="buildout-header" onClick={() => toggleSection(`buildout-${index}`)}>
                      <div className="buildout-icon">
                        <ComponentIcon className="w-6 h-6" />
                      </div>
                      <div className="buildout-info">
                        <h4>{component.title}</h4>
                        <p>{component.description}</p>
                      </div>
                      <button className="expand-btn">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    
                    {isExpanded && (
                      <div className="buildout-details">
                        <ul>
                          {component.details?.map((detail, i) => (
                            <li key={i}>
                              <CheckCircle2 className="w-4 h-4" />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* ROI Projection */}
          <motion.div
            className="audit-section roi-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="section-header">
              <div className="section-icon green">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <h2>Projected ROI</h2>
                <p>What you can expect with your AI Revenue Engine</p>
              </div>
            </div>
            
            <div className="roi-grid">
              <div className="roi-card main">
                <div className="roi-comparison">
                  <div className="roi-current">
                    <span className="roi-label">Current Revenue</span>
                    <span className="roi-value">{auditData.roi_projection?.current_monthly_revenue}</span>
                    <span className="roi-period">/month</span>
                  </div>
                  <div className="roi-arrow">
                    <ArrowRight className="w-8 h-8" />
                  </div>
                  <div className="roi-projected">
                    <span className="roi-label">Projected Revenue</span>
                    <span className="roi-value highlight">{auditData.roi_projection?.projected_monthly_revenue}</span>
                    <span className="roi-period">/month</span>
                  </div>
                </div>
                <div className="roi-increase">
                  <TrendingUp className="w-5 h-5" />
                  <span>{auditData.roi_projection?.increase_percentage} Increase</span>
                </div>
              </div>
              
              <div className="roi-stats">
                <div className="roi-stat">
                  <Users className="w-6 h-6" />
                  <span className="stat-value">{auditData.roi_projection?.leads_recovered}</span>
                  <span className="stat-label">Leads Recovered</span>
                </div>
                <div className="roi-stat">
                  <Clock className="w-6 h-6" />
                  <span className="stat-value">{auditData.roi_projection?.hours_saved}</span>
                  <span className="stat-label">Hours Saved</span>
                </div>
                <div className="roi-stat">
                  <RefreshCw className="w-6 h-6" />
                  <span className="stat-value">{auditData.roi_projection?.payback_period}</span>
                  <span className="stat-label">Payback Period</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="audit-cta">
              <div className="cta-content">
                <h3>Ready to Build Your AI Revenue Engine?</h3>
                <p>Book a strategy call and let's make this proposal a reality.</p>
              </div>
              
              <div className="cta-buttons">
                <button onClick={openBookingModal} className="cta-primary">
                  <Calendar className="w-5 h-5" />
                  Book a Strategy Call
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProposalView;
